/**
 * Service Mapper Module (TypeScript)
 * Interfaces with service_mapper canister for real service topology
 */

import { Result } from '../types/candid-types';

/**
 * Service information from the service_mapper canister
 */
interface ServiceInfo {
    bus_service_id: string;
    name: string;
    description: string;
    created_at: bigint;
}

/**
 * Service topology data structure combining service, devices, and connections
 */
interface ServiceTopology {
    service: ServiceInfo;
    devices: ServiceDevice[];
    connections: ServiceConnection[];
}

/**
 * Device information in service topology
 */
interface ServiceDevice {
    device_id: string;
    hostname: string | null;
    ip_address: string;
    role: string;
    tier: ServiceTier;
    accessible: boolean;
}

/**
 * Service tier variant type
 */
type ServiceTier =
    | { Entry: null }
    | { LoadBalancer: null }
    | { Application: null }
    | { Database: null }
    | { Storage: null }
    | { Network: null };

/**
 * Connection between services
 */
interface ServiceConnection {
    from_device: string;
    to_device: string;
    connection_type: string;
    port: number | null;
}

/**
 * Service mapper statistics from canister
 */
interface ServiceMapperStats {
    total_services: number;
    total_devices: number;
    total_sessions: number;
}

/**
 * Grouped devices by tier for visualization
 */
interface TierGroup {
    Entry: ServiceDevice[];
    LoadBalancer: ServiceDevice[];
    Application: ServiceDevice[];
    Database: ServiceDevice[];
    Storage: ServiceDevice[];
    Network: ServiceDevice[];
}

/**
 * Service map visualization data structure
 */
interface ServiceMapVisualization {
    service: ServiceInfo;
    tiers: TierGroup;
    connections: ServiceConnection[];
    totalDevices: number;
    healthyDevices: number;
}

/**
 * Service Mapper Client for interacting with service_mapper canister
 */
export class ServiceMapperClient {
    private canisterClient: CanisterClient | null;

    constructor() {
        this.canisterClient = null;
    }

    /**
     * Initialize the service mapper client with canister client instance
     * @param canisterClient - Initialized CanisterClient instance
     */
    async initialize(canisterClient: CanisterClient): Promise<void> {
        this.canisterClient = canisterClient;
        console.log('✅ Service Mapper client initialized');
    }

    /**
     * Get all business services from the canister
     * @returns Array of service information
     */
    async getAllServices(): Promise<ServiceInfo[]> {
        try {
            if (!this.canisterClient) {
                throw new Error('ServiceMapperClient not initialized');
            }

            const result = await this.canisterClient.callCanister<ServiceInfo[]>(
                'service_mapper',
                'list_services',
                []
            );
            console.log('📊 Got services:', result);
            return result;
        } catch (error) {
            console.error('❌ Error getting services:', error);
            return [];
        }
    }

    /**
     * Get service topology (service + devices + connections)
     * @param serviceId - The ID of the service to get topology for
     * @returns Service topology or null if error
     */
    async getServiceTopology(serviceId: string): Promise<ServiceTopology | null> {
        try {
            if (!this.canisterClient) {
                throw new Error('ServiceMapperClient not initialized');
            }

            const result = await this.canisterClient.callCanister<Result<[ServiceInfo, ServiceDevice[], ServiceConnection[]], string>>(
                'service_mapper',
                'get_service_topology',
                [serviceId]
            );

            if ('Ok' in result) {
                const [service, devices, connections] = result.Ok;
                return { service, devices, connections };
            } else {
                throw new Error(result.Err || 'Failed to get topology');
            }
        } catch (error) {
            console.error('❌ Error getting topology:', error);
            return null;
        }
    }

    /**
     * Get service mapper statistics
     * @returns Statistics object with counts
     */
    async getStats(): Promise<ServiceMapperStats> {
        try {
            if (!this.canisterClient) {
                throw new Error('ServiceMapperClient not initialized');
            }

            const result = await this.canisterClient.callCanister<[string, number][]>(
                'service_mapper',
                'get_stats',
                []
            );
            console.log('📊 Service Mapper stats:', result);
            return this.parseStats(result);
        } catch (error) {
            console.error('❌ Error getting stats:', error);
            return { total_services: 0, total_devices: 0, total_sessions: 0 };
        }
    }

    /**
     * Parse stats from Candid format (array of tuples) to object
     * @param candid - Array of [key, value] tuples
     * @returns Parsed statistics object
     */
    private parseStats(candid: [string, number][]): ServiceMapperStats {
        const stats: Partial<ServiceMapperStats> = {};
        if (Array.isArray(candid)) {
            candid.forEach(([key, value]) => {
                (stats as any)[key] = value;
            });
        }
        return stats as ServiceMapperStats;
    }

    /**
     * Build service map visualization data from topology
     * Groups devices by tier and calculates health metrics
     * @param topology - Service topology from canister
     * @returns Visualization-ready data structure or null
     */
    buildServiceMapVisualization(topology: ServiceTopology | null): ServiceMapVisualization | null {
        if (!topology) return null;

        const { service, devices, connections } = topology;

        // Group devices by tier
        const tiers: TierGroup = {
            Entry: [],
            LoadBalancer: [],
            Application: [],
            Database: [],
            Storage: [],
            Network: []
        };

        devices.forEach(device => {
            // Extract variant key from tier object
            const tierKey = Object.keys(device.tier)[0] as keyof TierGroup;
            if (tiers[tierKey]) {
                tiers[tierKey].push(device);
            }
        });

        return {
            service,
            tiers,
            connections,
            totalDevices: devices.length,
            healthyDevices: devices.filter(d => d.accessible).length
        };
    }
}

// Create global instance
declare global {
    interface Window {
        serviceMapperClient: ServiceMapperClient;
    }
}

window.serviceMapperClient = new ServiceMapperClient();

// Export for module usage
